import {
  Button,
  HStack,
  Stack,
  Text,
  Modal,
  ModalContent,
  ModalOverlay,
  Textarea,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Input,
  Divider,
} from "@chakra-ui/react";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";
import { AdminTicketType, KYDTicketType } from "@common/interfaces/KYDTicket";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { EditModalType, getRandomString } from "../utils";

import CurrencyInput from "react-currency-input-field";
import CodeSection from "./CodeSection";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

type ModalProps = {
  setModalType: (type: EditModalType) => void;
  loading: any;
  dirtyText: any;
  updateDirtyText: (params: any) => void;
  kydEvent: KYDAdminEvent;
  tiers: AdminTicketType[];
  onUpdateTicketType: (ticket_type_id: string) => void;
  onDeleteTicketType: (event_id: string, ticket_type_id: string) => void;
  error?: string
};

const EditTieredTypeModal = (props: ModalProps) => {
  const {
    setModalType,
    loading,
    error = '',
    dirtyText,
    updateDirtyText,
    kydEvent,
    tiers,
    onUpdateTicketType,
    onDeleteTicketType,
  } = props;

  const ticketType = tiers[0];

  return (
    <Modal
      size={"xl"}
      isOpen={true}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{"Edit Ticket Type"}</ModalHeader>
        <ModalBody>
          <Stack spacing={6}>
            <Stack>
              <Text fontWeight={"medium"}>Description</Text>
              <Textarea
                placeholder="Ticket type description"
                isDisabled={loading["tickettype"]}
                onChange={(e) => {
                  updateDirtyText({
                    [`tickettype.description`]: e.target.value,
                  });
                }}
                value={
                  `tickettype.description` in dirtyText
                    ? dirtyText[`tickettype.description`]
                    : ticketType.description
                }
              />
            </Stack>

            <Stack spacing={2}>
              <Text fontSize={"xl"} fontWeight={"bold"}>
                Timeline
              </Text>
              <Divider />

              <Stack
                direction={{ base: "column", md: "row" }}
                align={"center"}
                spacing={2}
              >
                <Stack maxW={"250px"} w="100%" spacing={0}>
                  <HStack>
                    <Text fontWeight={"medium"}>On Sale Date</Text>
                    <Text fontSize={"sm"} color={"gray.500"}>
                      {"(Optional)"}
                    </Text>
                  </HStack>
                  <DateTimePicker
                    disablePast
                    maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                    defaultValue={
                      `tickettype.on_sale_at` in dirtyText
                        ? dayjs(dirtyText[`tickettype.on_sale_at`])
                        : ticketType.on_sale_at
                        ? dayjs(ticketType.on_sale_at).tz(kydEvent?.timezone)
                        : null
                    }
                    timezone={kydEvent?.timezone}
                    onChange={(date) => {
                      updateDirtyText({
                        [`tickettype.on_sale_at`]: date?.toISOString(),
                      });
                    }}
                  />
                  <Text fontSize="sm" color="gray.500">
                    {kydEvent?.timezone}
                  </Text>
                </Stack>
                <Stack w="100%" maxW={"250px"} spacing={0}>
                  <HStack>
                    <Text fontWeight={"medium"}>Off Sale Date</Text>
                    <Text fontSize={"sm"} color={"gray.500"}>
                      {"(Optional)"}
                    </Text>
                  </HStack>
                  <DateTimePicker
                    disablePast
                    maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                    defaultValue={
                      `tickettype.off_sale_at` in dirtyText
                        ? dayjs(dirtyText[`tickettype.off_sale_at`])
                        : ticketType.off_sale_at
                        ? dayjs(ticketType.off_sale_at).tz(kydEvent?.timezone)
                        : null
                    }
                    timezone={kydEvent?.timezone}
                    onChange={(date) => {
                      updateDirtyText({
                        [`tickettype.off_sale_at`]: date?.toISOString(),
                      });
                    }}
                  />
                  <Text fontSize="sm" color="gray.500">
                    {kydEvent?.timezone}
                  </Text>
                </Stack>
              </Stack>
              <Stack
                direction={{ base: "column", md: "row" }}
                align={"center"}
                spacing={2}
              >
                <Stack maxW={"250px"} w="100%" spacing={0}>
                  <HStack>
                    <Text fontWeight={"medium"}>Valid From Date</Text>
                    <Text fontSize={"sm"} color={"gray.500"}>
                      {"(Optional)"}
                    </Text>
                  </HStack>
                  <DateTimePicker
                    minDate={dayjs(kydEvent?.start_at).tz(kydEvent?.timezone)}
                    maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                    defaultValue={
                      `tickettype.start_at` in dirtyText
                        ? dayjs(dirtyText[`tickettype.start_at`])
                        : ticketType.start_at
                        ? dayjs(ticketType.start_at)
                        : null
                    }
                    timezone={kydEvent?.timezone}
                    onChange={(date) => {
                      updateDirtyText({
                        [`tickettype.start_at`]: date?.toISOString(),
                      });
                    }}
                  />

                  <Text fontSize="sm" color="gray.500">
                    {kydEvent?.timezone}
                  </Text>
                </Stack>
                <Stack w="100%" maxW={"250px"} spacing={0}>
                  <HStack>
                    <Text fontWeight={"medium"}>Valid To Date</Text>
                    <Text fontSize={"sm"} color={"gray.500"}>
                      {"(Optional)"}
                    </Text>
                  </HStack>{" "}
                  <DateTimePicker
                    minDate={dayjs(kydEvent?.start_at).tz(kydEvent?.timezone)}
                    maxDate={dayjs(kydEvent?.end_at).tz(kydEvent?.timezone)}
                    defaultValue={
                      `tickettype.end_at` in dirtyText
                        ? dayjs(dirtyText[`tickettype.end_at`])
                        : ticketType.end_at
                        ? dayjs(ticketType.end_at)
                        : null
                    }
                    timezone={kydEvent?.timezone}
                    onChange={(date) => {
                      updateDirtyText({
                        [`tickettype.end_at`]: date?.toISOString(),
                      });
                    }}
                  />
                  <Text fontSize="sm" color="gray.500">
                    {kydEvent?.timezone}
                  </Text>
                </Stack>
              </Stack>
            </Stack>

            <CodeSection
              dirtyText={dirtyText}
              error={error}
              isLoading={loading.tickettypes}
              updateDirtyText={updateDirtyText}
              ticketType={ticketType}
              isTieredTicketType
            />

            <HStack w="100%" align={"top"} justify={"space-between"}>
              <Stack>
                <Text fontWeight={"medium"}>Limit Per Customer</Text>
                <Input
                  placeholder="Limit"
                  isDisabled={loading["tickettype"]}
                  maxW={"100px"}
                  inputMode="tel"
                  value={
                    `tickettype.limit` in dirtyText
                      ? dirtyText[`tickettype.limit`]
                      : ticketType.limit
                  }
                  onChange={(e) =>
                    updateDirtyText({
                      [`tickettype.limit`]: e.target.value,
                    })
                  }
                />
                <Text fontSize="sm" color="gray.500">
                  (Max 10)
                </Text>
              </Stack>
            </HStack>
            <Stack>
              <Stack w="100%">
                <HStack w="100%">
                  <Text fontWeight={"bold"} w="31%">
                    Name
                  </Text>
                  <Text fontWeight={"bold"} w="31%">
                    Quantity
                  </Text>
                  <Text fontWeight={"bold"} w="38%">
                    Price
                  </Text>
                </HStack>
                {tiers?.map((tier: KYDTicketType, index: number) => (
                  <Stack w="100%" key={index}>
                    <HStack>
                      <Input
                        isDisabled={tier.sold_out || loading["tickettype"]}
                        w="31%"
                        placeholder="Name"
                        value={
                          `${tier.id}.name` in dirtyText
                            ? dirtyText[`${tier.id}.name`]
                            : tier.name
                        }
                        onChange={(e) => {
                          updateDirtyText({
                            [`${tier.id}.name`]: e.target.value,
                          });
                        }}
                      />
                      <Input
                        isDisabled={tier.sold_out || loading["tickettype"]}
                        w="31%"
                        placeholder="Quantity"
                        color={
                          `${tier.id}.quantity` in dirtyText &&
                          parseInt(dirtyText[`${tier.id}.quantity`]) <
                            tier.quantity
                            ? "red.500"
                            : "inherit"
                        }
                        value={
                          `${tier.id}.quantity` in dirtyText
                            ? dirtyText[`${tier.id}.quantity`]
                            : tier.quantity
                        }
                        inputMode="tel"
                        onChange={(e) => {
                          updateDirtyText({
                            [`${tier.id}.quantity`]: e.target.value,
                          });
                        }}
                      />
                      <CurrencyInput
                        //@ts-ignore
                        isDisabled={tier.sold_out || loading["tickettype"]}
                        w="31%"
                        placeholder="Price"
                        value={
                          `${tier.id}.price` in dirtyText
                            ? dirtyText[`${tier.id}.price`]
                            : tier.price.units
                        }
                        onValueChange={(value) => {
                          updateDirtyText({
                            [`${tier.id}.price`]: value,
                          });
                        }}
                        intlConfig={{
                          locale: "en-US",
                          currency: kydEvent?.currency,
                        }}
                        customInput={Input}
                        step={1}
                      />
                    </HStack>
                    {`${tier.id}.quantity` in dirtyText &&
                    parseInt(dirtyText[`${tier.id}.quantity`]) <
                      tier.quantity ? (
                      <Text color="red.500">
                        Ticket quantity must be greater than {tier.quantity}{" "}
                        tickets
                      </Text>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={loading["tickettype"]}
            onClick={() => {
              updateDirtyText("all");
              setModalType(EditModalType.INACTIVE);
            }}
            mr="4"
          >
            Cancel
          </Button>
          {ticketType.status === "DRAFT" ? (
            <Button
              isLoading={loading["tickettype"]}
              bg="red.500"
              color="white"
              mr="4"
              onClick={() => onDeleteTicketType(kydEvent.id, ticketType.id)}
            >
              Delete
            </Button>
          ) : null}
          <Button
            color="white"
            bg="green.500"
            isDisabled={!!error}
            isLoading={loading["tickettype"]}
            onClick={() => onUpdateTicketType(ticketType.id)}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditTieredTypeModal;
